// Per-level / per-part configuration for standalone (single-part) practice tests.
//
// `def`       = default question count, taken from the Full Simulation structure
//               (backend `buildStructuredTestConfig`).
// `groupSize` = how many questions form one fixed group that shares an option
//               pool. groupSize === 1 means independent questions (any count is
//               allowed); groupSize > 1 means the requested count must be a whole
//               multiple of it, because the backend (`selectQuestions`) always
//               picks complete groups. Values verified against the production D1
//               `HSK_question_bank` (counts grouped by level/part/question_group).

export type TestPartKey = 'listening' | 'reading' | 'writing';

export interface PartDef {
  key: string;       // sub-part code sent to the backend, e.g. 'L1', 'R3', 'W2'
  label: string;     // shown on the config screen
  groupSize: number; // 1 = independent; >1 = must be a multiple
  def: number;       // default count (Full Simulation)
}

type LevelParts = Partial<Record<TestPartKey, PartDef[]>>;

export const TEST_PARTS: Record<number, LevelParts> = {
  2: {
    listening: [
      { key: 'L1', label: '第一部分 (L1)', groupSize: 1, def: 10 },
      { key: 'L2', label: '第二部分 (L2)', groupSize: 5, def: 10 },
      { key: 'L3', label: '第三部分 (L3)', groupSize: 1, def: 10 },
      { key: 'L4', label: '第四部分 (L4)', groupSize: 1, def: 5 },
    ],
    reading: [
      { key: 'R1', label: '第一部分 (R1)', groupSize: 5, def: 5 },
      { key: 'R2', label: '第二部分 (R2)', groupSize: 5, def: 5 },
      { key: 'R3', label: '第三部分 (R3)', groupSize: 1, def: 5 },
      { key: 'R4', label: '第四部分 (R4)', groupSize: 5, def: 10 },
    ],
    // HSK 2 has no writing section.
  },
  3: {
    listening: [
      { key: 'L1', label: '第一部分 (L1)', groupSize: 5, def: 10 },
      { key: 'L2', label: '第二部分 (L2)', groupSize: 1, def: 10 },
      { key: 'L3', label: '第三部分 (L3)', groupSize: 1, def: 10 },
      { key: 'L4', label: '第四部分 (L4)', groupSize: 1, def: 10 },
    ],
    reading: [
      { key: 'R1', label: '第一部分 (R1)', groupSize: 5, def: 10 },
      { key: 'R2', label: '第二部分 (R2)', groupSize: 5, def: 5 },
      { key: 'R3', label: '第三部分 (R3)', groupSize: 5, def: 5 },
      { key: 'R4', label: '第四部分 (R4)', groupSize: 1, def: 10 },
    ],
    writing: [
      { key: 'W1', label: 'W1 组句', groupSize: 1, def: 5 },
      { key: 'W2', label: 'W2 填空', groupSize: 1, def: 5 },
    ],
  },
  4: {
    listening: [
      { key: 'L1', label: '第一部分 (L1)', groupSize: 1, def: 10 },
      { key: 'L2', label: '第二部分 (L2)', groupSize: 1, def: 15 },
      { key: 'L3', label: '第三部分 (L3)', groupSize: 1, def: 10 },
      { key: 'L4', label: '第四部分 (L4)', groupSize: 2, def: 10 },
    ],
    reading: [
      { key: 'R1', label: '第一部分 (R1)', groupSize: 5, def: 5 },
      { key: 'R2', label: '第二部分 (R2)', groupSize: 5, def: 5 },
      { key: 'R3', label: '第三部分 (R3) 排序', groupSize: 1, def: 10 },
      { key: 'R4', label: '第四部分 (R4)', groupSize: 1, def: 14 },
      { key: 'R5', label: '第五部分 (R5)', groupSize: 2, def: 6 },
    ],
    writing: [
      { key: 'W1', label: 'W1 组句', groupSize: 1, def: 10 },
      { key: 'W2', label: 'W2 看图写句', groupSize: 1, def: 5 },
    ],
  },
};

// Parse the route :level param ("hsk2" / "3" / "hsk4") into a supported number.
export const parseLevel = (raw?: string | null): number => {
  const n = Number(String(raw || '').replace(/\D/g, ''));
  return n === 2 || n === 3 || n === 4 ? n : 3;
};

export const getParts = (level: number, part: TestPartKey): PartDef[] =>
  TEST_PARTS[level]?.[part] ?? [];

export const hasWriting = (level: number): boolean =>
  (TEST_PARTS[level]?.writing?.length ?? 0) > 0;

// Default config map { L1: 10, ... } for a given level/part.
export const getDefaults = (level: number, part: TestPartKey): Record<string, number> =>
  getParts(level, part).reduce((acc, p) => ({ ...acc, [p.key]: p.def }), {} as Record<string, number>);

// Round a requested count to a valid value for a (possibly grouped) sub-part.
export const snapToGroup = (val: number, groupSize: number): number => {
  const n = Math.max(0, Math.floor(Number(val) || 0));
  if (groupSize <= 1) return n;
  return Math.round(n / groupSize) * groupSize;
};
